import { Currency } from 'domain/Currency/Currency'
import { isAddress, PlacesSearchType, PortOrAddress, TLocationPoint } from 'features/Directory'
import { ShipmentInterface, ShipmentMode, ShipmentType } from 'features/ShipmentDetails'
import { isEqual } from 'lodash-es'
import moment from 'moment'
import { DefaultValues } from 'react-hook-form'
import { ISO } from 'service/ISO'
import { meterToCentimeter } from 'service/NumberFormatter'
import { DateFormat } from 'utils/date'

import { Cargo, CONTAINER_TYPE_45GP, FCLCargoJSON, LCLCargoJSON } from '../Cargo'
import { CARRIAGE_MODE_AIR, CARRIAGE_MODE_RAIL, CARRIAGE_MODE_SEA, CARRIAGE_MODE_SEA_AIR } from '../Carriage'
import { CustomsType } from '../CustomsType'
import { IQuoteRequest } from '../fromMobx'
import { BookingParty } from '../Incoterms/incotermsMeta'
import { IInsurance } from '../InsuranceModel'
import { InsuranceQR, insuranceTypes, serializeInsuranceQR, toInsuranceQR } from '../QuoteRequestInsurance'
import { CarbonOffsetStrategy } from '../QuoteServices'
import { IQuoteSheet } from '../QuoteSheet/QuoteSheetModel'
import { BuyersConsolidationQuoteServicesJSON } from './BuyersConsolidationQuoteServicesJSON'

interface QuoteRequestJSON {
  _id?: string
  cargo: Cargo[]
  cargoReadyAt?: Date | null
  customs: boolean
  customsIssuance?: boolean
  dangerousGoods?: boolean
  dangerousGoodsAdditionalDetails?: string
  destination?: string
  hsCodesCount: number
  insurance: IInsurance[]
  modes: ShipmentMode[]
  origin?: string
  pod?: string
  pol?: string
  requestedCustomsTypes: CustomsType[]
  specialEquipment: boolean
  type: ShipmentType
  vgm?: boolean
  buyersConsolidation?: BuyersConsolidationQuoteServicesJSON
  customsBondType?: string
  incoTerm?: ISO.IncoTermsCode
  bookingParty?: BookingParty
  carbonOffsetService?: {
    offsetStrategy: CarbonOffsetStrategy
  }
  isBookingPartyAgent?: boolean
}

interface LCLQuoteRequestSerialized extends QuoteRequestJSON {
  type: 'lcl'
  modes: ShipmentMode[]
  cargo: LCLCargoJSON[]
  cargoReadyAt: Date
  isStackable: boolean
  goodsValue: Currency
}

export interface QuoteRequestFormState {
  cargoFcl: FCLCargoJSON[]
  cargoLcl: LCLCargoJSON[]
  cargoReadyAt: Date | null
  cargoStackable?: boolean
  customs: boolean
  customsBondType?: string
  customsIssuance?: boolean
  dangerousGoods?: boolean
  dangerousGoodsAdditionalDetails?: string
  destination: PortOrAddress

  goodsValue?: Currency
  hsCodesCount?: number
  insurance: InsuranceQR[]
  insuranceEnabled: boolean
  modes: ShipmentMode[]
  origin: PortOrAddress
  specialEquipment?: boolean
  type: ShipmentType
  vgm?: boolean
  buyersConsolidation?: BuyersConsolidationQuoteServicesJSON
  incoTerm?: ISO.IncoTermsCode
  bookingParty?: BookingParty
  carbonOffsetService?: {
    offsetStrategy: CarbonOffsetStrategy
  }
  isBookingPartyAgent?: boolean
}

export const defaultFclCargo: FCLCargoJSON = {
  type: 'fcl',
  quantity: 1,
  containerType: CONTAINER_TYPE_45GP,
}

export const defaultLclCargo: LCLCargoJSON = {
  type: 'lcl',
  quantity: 1,
  grossWeight: 0,
  volume: 0,
  height: 0,
  width: 0,
  length: 0,
}

const extractInsurances = (qr: QuoteRequestFormState): IInsurance[] => {
  return qr.insuranceEnabled ? serializeInsuranceQR(qr.insurance) : []
}

export const preProcessLclCargo = (lclCargo: LCLCargoJSON[]): LCLCargoJSON[] => {
  return lclCargo.map((cargo) => {
    // Convert to centimeters
    if (cargo.width && cargo.length && cargo.height) {
      return {
        ...cargo,
        width: meterToCentimeter(Number(cargo.width)),
        length: meterToCentimeter(Number(cargo.length)),
        height: meterToCentimeter(Number(cargo.height)),
      }
    }
    return cargo
  })
}

export const postProcessLclCargo = (lclCargo: LCLCargoJSON[]): LCLCargoJSON[] => {
  return lclCargo.map((cargo) => {
    // Convert back to meters
    if (cargo.width && cargo.length && cargo.height) {
      return {
        ...cargo,
        width: Number(cargo.width) / 100,
        length: Number(cargo.length) / 100,
        height: Number(cargo.height) / 100,
      }
    }
    return cargo
  })
}

export const fromQuoteSheet = (sheet: IQuoteSheet): QuoteRequestFormState => {
  const { request } = sheet
  const quoteRequest: QuoteRequestFormState = {
    type: request.type,
    modes: request.modes,
    origin: request.journey.origin,
    destination: request.journey.destination,
    cargoFcl: request.type === 'fcl' ? (request.cargo as FCLCargoJSON[]) : [defaultFclCargo],
    cargoLcl: request.type === 'lcl' ? preProcessLclCargo(request.cargo as LCLCargoJSON[]) : [defaultLclCargo],
    cargoStackable: request.services?.isStackable,
    cargoReadyAt: request.cargoReadyAt ? new Date(request.cargoReadyAt) : null,
    customs:
      request.services?.customsSelected || (!!request.services?.customsIssuance && !!request.services.customsBondType),
    hsCodesCount: request.services?.hsCodesCount,
    insuranceEnabled: request.services?.insurances ? request.services.insurances.length > 0 : false,
    insurance: toInsuranceQR(request.services?.insurances ?? []),
    specialEquipment: request.services?.specialEquipment,
    dangerousGoods: request.services?.dangerousGoods,
    dangerousGoodsAdditionalDetails: request.services?.dangerousGoodsAdditionalDetails,
    goodsValue: request.goodsValue,
    customsIssuance: request.services?.customsIssuance,
    customsBondType: request.services?.customsBondType || '',
    vgm: request.services?.vgm,
    buyersConsolidation: request.services?.buyersConsolidation,
    incoTerm: request.incoTerm,
    bookingParty: request.bookingParty,
    isBookingPartyAgent: request.isBookingPartyAgent,
    carbonOffsetService: request.services?.carbonOffset,
  }
  return quoteRequest
}

export const fromShipment = (shipment: ShipmentInterface): IQuoteRequest => {
  const modes = [shipment.mode]
  const cargoReadyAt = moment().add(2, 'day').toDate()
  const insuranceEnabled = shipment.insurance && shipment.insurance.length > 0
  const insurance = toInsuranceQR(shipment.insurance)
  const customs = !!(shipment.originCustomsType || shipment.destinationCustomsType)
  const offsetStrategy =
    (shipment.quoteOption?.carbonOffsetQuote?.lineItems?.length ?? 0) > 0
      ? CarbonOffsetStrategy.All
      : CarbonOffsetStrategy.None

  const quoteRequest: IQuoteRequest = {
    ...shipment,
    cargo: shipment.cargo,
    customs,
    insuranceEnabled,
    insurance,
    cargoReadyAt,
    modes,
    customsIssuance: shipment.customsIssuance,
    goodsValue: shipment.goodsValue,
    customsBondType: shipment.customsBondType,
    vgm: shipment.vgm,
    incoTerm: shipment.quoteOption?.incoTerm,
    bookingParty: shipment.quoteOption?.bookingParty,
    carbonOffsetService: {
      offsetStrategy,
    },
    isBookingPartyAgent: shipment.quoteOption?.isBookingPartyAgent,
  } as IQuoteRequest
  return quoteRequest
}

export type QuoteSearchPayload = Omit<QuoteRequestFormState, 'cargoFcl' | 'cargoLcl' | 'cargoReadyAt'> & {
  cargo: Cargo[]
  cargoReadyAt: string | null
}
export const mapFormStateToPayload = (qr: QuoteRequestFormState): QuoteSearchPayload => {
  const { cargoFcl, cargoLcl, cargoReadyAt, ...rest } = qr

  return {
    ...rest,
    cargoReadyAt: cargoReadyAt ? moment(cargoReadyAt).format(DateFormat.ISO_DATE).toString() : null,
    cargo: qr.type === 'lcl' ? postProcessLclCargo(cargoLcl) : cargoFcl,
  }
}

export const mapPayloadToFormState = (payload: QuoteSearchPayload): QuoteRequestFormState => {
  return {
    ...payload,
    cargoReadyAt: payload.cargoReadyAt ? new Date(payload.cargoReadyAt) : null,
    cargoFcl: payload.type === 'fcl' ? (payload.cargo as FCLCargoJSON[]) : [defaultFclCargo],
    cargoLcl: payload.type === 'lcl' ? preProcessLclCargo(payload.cargo as LCLCargoJSON[]) : [defaultLclCargo],
  }
}

interface QROrigin {
  pol?: string
  origin?: string
  originLocationId?: string
}

interface QRDestination {
  pod?: string
  destination?: string
  destinationLocationId?: string
}

abstract class Builder<T extends QRDestination | QROrigin> {
  transformPlace(place: PortOrAddress): this {
    // Template method to transform place
    if (isAddress(place)) {
      if (place.locationId) {
        this.addLocation(place.locationId)
      } else {
        this.addPlace(place.place_id || place.googplace_id || place.googlePlace)
      }
    } else {
      this.addPort(place._id)
    }
    return this
  }
  protected abstract addLocation(locationId: string): void
  protected abstract addPlace(place?: string): void
  protected abstract addPort(port: string): void
  abstract build(): T
}

class QROriginBuilder extends Builder<QROrigin> {
  private origin: QROrigin = {}

  addLocation(locationId: string): void {
    this.origin.originLocationId = locationId
  }

  addPlace(place: string): void {
    this.origin.origin = place
  }

  addPort(port: string): void {
    this.origin.pol = port
  }

  build(): QROrigin {
    return this.origin
  }
}

class QRDestinationBuilder extends Builder<QRDestination> {
  private destination: QRDestination = {}

  addLocation(locationId: string): void {
    this.destination.destinationLocationId = locationId
  }

  addPlace(place: string): void {
    this.destination.destination = place
  }

  addPort(port: string): void {
    this.destination.pod = port
  }

  build(): QRDestination {
    return this.destination
  }
}

function serializeDestination(place: PortOrAddress): QRDestination {
  return new QRDestinationBuilder().transformPlace(place).build()
}

function serializeOrigin(place: PortOrAddress): QROrigin {
  return new QROriginBuilder().transformPlace(place).build()
}

export const customsTypesToJSON = (customs: boolean): CustomsType[] => {
  const customsTypes: CustomsType[] = []
  if (customs) {
    customsTypes.push('standard', 'fiscal', 't1', 't1_only')
  }
  return customsTypes
}

export const serializeRequest = (qr: QuoteRequestFormState): QuoteRequestJSON => {
  const cargo: Cargo[] = qr.type === 'lcl' ? qr.cargoLcl : qr.cargoFcl
  let qrs: QuoteRequestJSON = {
    type: qr.type,
    modes: qr.modes && qr.modes.length > 0 ? qr.modes : ['sea'],
    customs: qr.customs,
    requestedCustomsTypes: customsTypesToJSON(qr.customs),
    insurance: extractInsurances(qr),
    cargo: cargo.filter((c) => c.type === qr.type),
    hsCodesCount: qr.hsCodesCount ? Number(qr.hsCodesCount) : 1,
    specialEquipment: !!qr.specialEquipment,
    dangerousGoods: !!qr.dangerousGoods,
    dangerousGoodsAdditionalDetails: qr.dangerousGoodsAdditionalDetails,
    ...serializeDestination(qr.destination),
    ...serializeOrigin(qr.origin),
    customsIssuance: qr.customsIssuance,
    buyersConsolidation: qr.buyersConsolidation,
    cargoReadyAt: qr.cargoReadyAt,
    customsBondType: qr.customsBondType,
    incoTerm: qr.incoTerm,
    bookingParty: qr.bookingParty,
    carbonOffsetService: qr.carbonOffsetService,
    isBookingPartyAgent: qr.isBookingPartyAgent,
  }
  if (qr.type === 'fcl') {
    qrs = {
      ...qrs,
      vgm: qr.vgm,
    }
  } else if (qr.type === 'lcl') {
    qrs = {
      ...qrs,
      isStackable: qr.cargoStackable,
      goodsValue: qr.goodsValue,
    } as LCLQuoteRequestSerialized
  }
  return qrs
}

export const getUrlQuoteRequest = (windowLocationSearch: string): QuoteRequestJSON => {
  const urlParams = new URLSearchParams(windowLocationSearch)
  const request = urlParams.get('request') ?? ''
  return JSON.parse(request)
}

export const sheetMatchesUrlRequest = (sheet: IQuoteSheet, urlRequest: QuoteRequestJSON): boolean => {
  return isEqual(serializeRequest(fromQuoteSheet(sheet)), urlRequest)
}

export const getLocationCountryCode = (location?: PortOrAddress): string | undefined => {
  if (!location) {
    return
  }

  return location.countryCode || (location as TLocationPoint).country
}

const hasAir = (transportModes: ShipmentMode[]) => transportModes.includes(CARRIAGE_MODE_AIR)
const hasSeaAir = (transportModes: ShipmentMode[]) => transportModes.includes(CARRIAGE_MODE_SEA_AIR)
export const hasAirOrSeaAir = (transportModes: ShipmentMode[]) => hasAir(transportModes) || hasSeaAir(transportModes)

export const isSea = (transportModes: ShipmentMode[]): boolean =>
  transportModes.length === 1 && transportModes[0] === CARRIAGE_MODE_SEA
export const isAir = (transportModes: ShipmentMode[]): boolean =>
  transportModes.length === 1 && transportModes[0] === CARRIAGE_MODE_AIR
export const isSeaAir = (transportModes: ShipmentMode[]): boolean =>
  transportModes.length === 1 && transportModes[0] === CARRIAGE_MODE_SEA_AIR
export const isRail = (transportModes: ShipmentMode[]): boolean =>
  transportModes.length === 1 && transportModes[0] === CARRIAGE_MODE_RAIL

const getOriginPortType = (mode: ShipmentMode) => (mode === 'seaAir' ? 'sea' : mode)
const getDestinationPortType = (mode: ShipmentMode) => (mode === 'seaAir' ? 'air' : mode)
export const getOriginTypes = (transportModes: ShipmentMode[]): PlacesSearchType[] => {
  return ['address', ...transportModes.map((mode) => getOriginPortType(mode))]
}
export const getDestinationTypes = (transportModes: ShipmentMode[]): PlacesSearchType[] => {
  return ['address', ...transportModes.map((mode) => getDestinationPortType(mode))]
}

type isRestrictedCountryArgs = {
  allowSyria: boolean
  originCountryCode?: string
  destinationCountryCode?: string
}

// Russia, Ukraine, Belrus, Cuba, Iran, Iraq, North Korea
export const restrictedCountries = ['RU', 'UA', 'BY', 'CU', 'IR', 'IQ', 'KP']
export const isRestrictedCountry = ({
  allowSyria,
  originCountryCode = '',
  destinationCountryCode = '',
}: isRestrictedCountryArgs): boolean => {
  // Syria should also be inlcuded in the list of unrestricted countries unless specifically overridden using a feature flag
  if (!allowSyria) {
    restrictedCountries.push('SY')
  }
  return restrictedCountries.includes(originCountryCode) || restrictedCountries.includes(destinationCountryCode)
}

type GetCargoVolumeArgs = {
  height?: number
  length?: number
  width?: number
}
// Get cargo volume in cubic meters
export const calculateVolumeInCubicMeters = ({ height, width, length }: GetCargoVolumeArgs): number => {
  return ((height || 0) * (width || 0) * (length || 0)) / 1000000
}

export const defaultQuoteRequest: DefaultValues<QuoteRequestFormState> = {
  modes: ['sea'],
  type: 'fcl',
  bookingParty: BookingParty.Buyer,
  incoTerm: 'FOB',
  cargoFcl: [defaultFclCargo],
  cargoLcl: [defaultLclCargo],
  insuranceEnabled: false,
  insurance: insuranceTypes,
  cargoStackable: true,
  hsCodesCount: 1,
  specialEquipment: false,
  dangerousGoods: false,
  vgm: false,
  isBookingPartyAgent: false,
}
